import React, { useState, useEffect, useRef } from "react";
import { FaArrowRight, FaCopy, FaCheck, FaArrowLeft, FaArrowRight as FaNext } from "react-icons/fa";
import "./Python.css";

function Python() {
  const text = "Welcome Python-Django Dev";
  const [displayedText, setDisplayedText] = useState("");
  const [charIndex, setCharIndex] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [copied, setCopied] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (charIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.substring(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [charIndex, text]);

  const services = [

    {
  id: 1,
  title: "M-Pesa Integration",
  content: `
### M-Pesa Integration in Django

#### Overview
Integrating M-Pesa payments into your Django application involves using the **Daraja API** provided by Safaricom. This guide will walk you through the process of setting up M-Pesa payments, including generating access tokens, sending STK Push requests, handling callbacks, and managing offline payments.

---

### Step 1: Setting Up Your Django Project
1. **Install Django**: If you haven't already, install Django using pip:
   \`\`\`bash
   pip install django
   \`\`\`

2. **Create a Django Project**:
   \`\`\`bash
   django-admin startproject mpesa_integration
   cd mpesa_integration
   \`\`\`

3. **Create a Django App**:
   \`\`\`bash
   python manage.py startapp payments
   \`\`\`

4. **Add the App to Installed Apps**:
   In (settings.py), add (payments) to the (INSTALLED_APPS) list.

---

### Step 2: Obtaining Daraja API Credentials
To use the Daraja API, you need the following credentials:
- **Consumer Key**
- **Consumer Secret**
- **PassKey**

You can obtain these credentials by creating an app on the [Daraja Portal](https://developer.safaricom.co.ke/).

---

### Step 3: Generating an Access Token
To interact with the Daraja API, you need an access token. Here's how to generate it in Django:

\`\`\`python
# payments/utils.py
import requests
from django.conf import settings

def get_access_token():
    consumer_key = settings.MPESA_CONSUMER_KEY
    consumer_secret = settings.MPESA_CONSUMER_SECRET
    api_url = "https://sandbox.safaricom.co.ke/oauth/v1/generate?grant_type=client_credentials"  # Sandbox URL
    # api_url = "https://api.safaricom.co.ke/oauth/v1/generate?grant_type=client_credentials"  # Live URL

    try:
        response = requests.get(api_url, auth=(consumer_key, consumer_secret))
        response.raise_for_status()
        return response.json().get('access_token')
    except requests.exceptions.RequestException as e:
        print(f"Error getting access token: {e}")
        return None
\`\`\`

---

### Step 4: Sending an STK Push Request
To send an STK Push request, use the following code:

\`\`\`python
# payments/utils.py
import base64
import datetime
import requests
from django.conf import settings

def send_stk_push(phone, amount):
    access_token = get_access_token()
    if not access_token:
        return None

    short_code = settings.MPESA_SHORTCODE
    passkey = settings.MPESA_PASSKEY
    timestamp = datetime.datetime.now().strftime('%Y%m%d%H%M%S')
    password = base64.b64encode(f"{short_code}{passkey}{timestamp}".encode()).decode()

    api_url = "https://sandbox.safaricom.co.ke/mpesa/stkpush/v1/processrequest"  # Sandbox URL
    # api_url = "https://api.safaricom.co.ke/mpesa/stkpush/v1/processrequest"  # Live URL

    headers = {
        "Authorization": f"Bearer {access_token}",
        "Content-Type": "application/json",
    }

    payload = {
        "BusinessShortCode": short_code,
        "Password": password,
        "Timestamp": timestamp,
        "TransactionType": "CustomerPayBillOnline",  # For Till: "CustomerBuyGoodsOnline"
        "Amount": amount,
        "PartyA": phone,
        "PartyB": short_code,
        "PhoneNumber": phone,
        "CallBackURL": "https://yourdomain.com/callback/",  # Replace with your callback URL
        "AccountReference": "Test",
        "TransactionDesc": "Test Payment",
    }

    try:
        response = requests.post(api_url, json=payload, headers=headers)
        response.raise_for_status()
        return response.json()
    except requests.exceptions.RequestException as e:
        print(f"Error sending STK Push: {e}")
        return None
\`\`\`

---

### Step 5: Handling Callback Data
M-Pesa will send a callback to your server once the payment is processed. Here's how to handle it in Django:

\`\`\`python
# payments/views.py
from django.http import JsonResponse
from django.views.decorators.csrf import csrf_exempt
import json
import os

@csrf_exempt
def mpesa_callback(request):
    if request.method == 'POST':
        try:
            data = json.loads(request.body)
            # Save the callback data to a file
            file_path = os.path.join('callback_data', f"callback_{datetime.datetime.now().timestamp()}.json")
            with open(file_path, 'w') as f:
                json.dump(data, f)

            # Process the callback data
            result_code = data.get('Body', {}).get('stkCallback', {}).get('ResultCode')
            if result_code == 0:
                # Payment was successful
                metadata = data.get('Body', {}).get('stkCallback', {}).get('CallbackMetadata', {}).get('Item', [])
                amount = next((item['Value'] for item in metadata if item['Name'] == 'Amount'), None)
                phone_number = next((item['Value'] for item in metadata if item['Name'] == 'PhoneNumber'), None)
                mpesa_receipt = next((item['Value'] for item in metadata if item['Name'] == 'MpesaReceiptNumber'), None)

                # Save the transaction to your database
                # ...

            return JsonResponse({"status": "success"})
        except Exception as e:
            print(f"Error processing callback: {e}")
            return JsonResponse({"status": "error", "message": str(e)}, status=500)
    return JsonResponse({"status": "error", "message": "Invalid request method"}, status=400)
\`\`\`

---

### Step 6: Registering URLs for C2B API
To handle offline payments, you need to register validation and confirmation URLs with the Daraja API:

\`\`\`python
# payments/utils.py
def register_urls():
    access_token = get_access_token()
    if not access_token:
        return None

    api_url = "https://sandbox.safaricom.co.ke/mpesa/c2b/v1/registerurl"  # Sandbox URL
    # api_url = "https://api.safaricom.co.ke/mpesa/c2b/v1/registerurl"  # Live URL

    headers = {
        "Authorization": f"Bearer {access_token}",
        "Content-Type": "application/json",
    }

    payload = {
        "ShortCode": settings.MPESA_SHORTCODE,
        "ResponseType": "Completed",  # or "Cancelled"
        "ConfirmationURL": "https://yourdomain.com/confirmation/",  # Replace with your confirmation URL
        "ValidationURL": "https://yourdomain.com/validation/",  # Replace with your validation URL
    }

    try:
        response = requests.post(api_url, json=payload, headers=headers)
        response.raise_for_status()
        return response.json()
    except requests.exceptions.RequestException as e:
        print(f"Error registering URLs: {e}")
        return None
\`\`\`

---

### Step 7: Handling C2B Webhook Data
When a payment is made to your Paybill or Till number, M-Pesa will send a webhook to your confirmation URL. Here's how to handle it:

\`\`\`python
# payments/views.py
@csrf_exempt
def c2b_confirmation(request):
    if request.method == 'POST':
        try:
            data = json.loads(request.body)
            # Process the payment data
            transaction_type = data.get('TransactionType')
            transaction_id = data.get('TransID')
            amount = data.get('TransAmount')
            phone_number = data.get('MSISDN')

            # Save the transaction to your database
            # ...

            return JsonResponse({"status": "success"})
        except Exception as e:
            print(f"Error processing C2B confirmation: {e}")
            return JsonResponse({"status": "error", "message": str(e)}, status=500)
    return JsonResponse({"status": "error", "message": "Invalid request method"}, status=400)
\`\`\`

---

### Conclusion
With these steps, you can successfully integrate M-Pesa payments into your Django application. You can now send STK Push requests, handle callbacks, and manage offline payments using the Daraja API.

Happy coding! 🚀
`
},
{
  id: 2,
  title: "Web Development",
  content: `
### Getting Started with Django for Building Websites

#### Overview
Django is a high-level Python web framework that enables rapid development of secure and maintainable websites. This guide will walk you through the steps to get started with Django, from setting up your environment to deploying your site.

---

### Step 1: Set Up Your Development Environment
1. **Install Python**: Ensure Python is installed on your system. You can download it from [python.org](https://www.python.org/downloads/).
2. **Install Django**: Use pip to install Django:
   \`\`\`bash
   pip install django
   \`\`\`
3. **Set Up a Virtual Environment**: Create a virtual environment to isolate your project dependencies:
   \`\`\`bash
   python -m venv myenv
   source myenv/bin/activate  # On Windows: myenv\\Scripts\\activate
   \`\`\`
4. **Install a Text Editor/IDE**: Use a code editor like **Visual Studio Code**, **PyCharm**, or **Sublime Text**.

---

### Step 2: Create a Django Project
1. **Create a Project**:
   \`\`\`bash
   django-admin startproject myproject
   cd myproject
   \`\`\`
2. **Create an App**:
   \`\`\`bash
   python manage.py startapp myapp
   \`\`\`
3. **Add the App to Installed Apps**:
   In (settings.py), add (myapp) to the (INSTALLED_APPS) list.

---

### Step 3: Learn Django Basics
1. **Django Syntax**:
   Understand basic Django syntax, including views, URLs, and templates.
   \`\`\`python
   # myapp/views.py
   from django.http import HttpResponse

   def home(request):
       return HttpResponse("Hello, World!")
   \`\`\`
2. **URL Routing**:
   Map URLs to views in (urls.py):
   \`\`\`python
   # myapp/urls.py
   from django.urls import path
   from . import views

   urlpatterns = [
       path('', views.home, name='home'),
   ]
   \`\`\`
3. **Templates**:
   Use Django's templating engine to render HTML:
   \`\`\`html
   <!-- myapp/templates/home.html -->
   <h1>Hello, World!</h1>
   \`\`\`
   Update the view to render the template:
   \`\`\`python
   # myapp/views.py
   from django.shortcuts import render

   def home(request):
       return render(request, 'home.html')
   \`\`\`

---

### Step 4: Database Interaction (PostgreSQL/MySQL/SQLite)
1. **Set Up a Database**:
   Configure your database in (settings.py):
   \`\`\`python
   DATABASES = {
       'default': {
           'ENGINE': 'django.db.backends.sqlite3',
           'NAME': BASE_DIR / 'db.sqlite3',
       }
   }
   \`\`\`
2. **Create Models**:
   Define your database models in (models.py):
   \`\`\`python
   # myapp/models.py
   from django.db import models

   class Post(models.Model):
       title = models.CharField(max_length=100)
       content = models.TextField()
       created_at = models.DateTimeField(auto_now_add=True)

       def __str__(self):
           return self.title
   \`\`\`
3. **Run Migrations**:
   Apply your models to the database:
   \`\`\`bash
   python manage.py makemigrations
   python manage.py migrate
   \`\`\`
4. **CRUD Operations**:
   Use Django's ORM to perform CRUD operations:
   \`\`\`python
   # Create
   post = Post(title='My First Post', content='Hello, Django!')
   post.save()

   # Read
   posts = Post.objects.all()

   # Update
   post = Post.objects.get(id=1)
   post.title = 'Updated Title'
   post.save()

   # Delete
   post = Post.objects.get(id=1)
   post.delete()
   \`\`\`

---

### Step 5: Work with Sessions and Cookies
1. **Sessions**:
   Use Django's session framework to store user data:
   \`\`\`python
   request.session['username'] = 'JohnDoe'
   username = request.session.get('username')
   \`\`\`
2. **Cookies**:
   Set and retrieve cookies:
   \`\`\`python
   response = HttpResponse("Hello, World!")
   response.set_cookie('user', 'JohnDoe', max_age=3600)  # Expires in 1 hour
   user = request.COOKIES.get('user')
   \`\`\`

---

### Step 6: Learn About Django Frameworks (Optional)
1. **Why Use Frameworks?** Frameworks like **Django REST Framework (DRF)** provide tools for building APIs and extending Django's functionality.
2. **Start with DRF**: Install DRF and create a simple API:
   \`\`\`bash
   pip install djangorestframework
   \`\`\`
   Add (rest_framework) to (INSTALLED_APPS) in (settings.py).

---

### Step 7: Design Your Website (HTML & CSS)
1. **HTML**: Use Django templates to create the structure of your website.
2. **CSS**: Style your website using CSS or frameworks like **Bootstrap**.

---

### Step 8: Create Dynamic Content
1. **Dynamic Pages**:
   Use Django's ORM to fetch and display dynamic content:
   \`\`\`python
   # myapp/views.py
   from django.shortcuts import render
   from .models import Post

   def blog(request):
       posts = Post.objects.all()
       return render(request, 'blog.html', {'posts': posts})
   \`\`\`
   \`\`\`html
   <!-- myapp/templates/blog.html -->
   {% for post in posts %}
       <h2>{{ post.title }}</h2>
       <p>{{ post.content }}</p>
   {% endfor %}
   \`\`\`

---

### Step 9: Add Forms and Validation
1. **Create Forms**:
   Use Django's form framework to create and process forms:
   \`\`\`python
   # myapp/forms.py
   from django import forms

   class ContactForm(forms.Form):
       name = forms.CharField(max_length=100)
       email = forms.EmailField()
       message = forms.CharField(widget=forms.Textarea)
   \`\`\`
2. **Validation**:
   Validate form data in the view:
   \`\`\`python
   # myapp/views.py
   from django.shortcuts import render, redirect
   from .forms import ContactForm

   def contact(request):
       if request.method == 'POST':
           form = ContactForm(request.POST)
           if form.is_valid():
               # Process the data
               return redirect('home')
       else:
           form = ContactForm()
       return render(request, 'contact.html', {'form': form})
   \`\`\`

---

### Step 10: Security Measures
1. **Sanitize User Input**: Use Django's built-in protections against SQL injection and XSS attacks.
2. **Password Hashing**: Use Django's (make_password) and (check_password) (functions):
   \`\`\`python
   from django.contrib.auth.hashers import make_password, check_password
   hashed_password = make_password('mypassword')
   is_valid = check_password('mypassword', hashed_password)
   \`\`\`

---

### Step 11: Testing Your Website
1. **Local Testing**: Use Django's development server:
   \`\`\`bash
   python manage.py runserver
   \`\`\`
2. **Debugging**: Use Django's debugging tools to troubleshoot issues.
3. **Browser Testing**: Test your website on different browsers.

---

### Step 12: Deploy Your Website
1. **Choose a Hosting Provider**: Use platforms like **Heroku**, **AWS**, or **DigitalOcean**.
2. **Upload Your Files**: Use Git or FTP to deploy your Django project.
3. **Configure Database**: Set up your database on the hosting server.
4. **Test Live Website**: Ensure all functionalities work in the production environment.

---

### Step 13: Maintain and Update Your Website
1. **Bug Fixes**: Regularly fix bugs and issues.
2. **Security Updates**: Keep Django and dependencies updated.

---

### Additional Tips:
1. **Learn Version Control**: Use Git to track changes and collaborate with others.
2. **Documentation**: Regularly document your code for future reference.
3. **Community**: Engage with Django communities (forums, StackOverflow, etc.) to learn and ask questions.

By following this structured approach, you'll gain proficiency in building web applications with Django. Start small, build projects, and gradually explore advanced topics!
`
},
    {
      id: 3,
      title: "SMS Integration",
      content: `

      Django Explanation of SMS Integration
Obtain API Credentials

Sign up on UmeskiaSoftwares UMSComms portal.
 pay as you require then

Generate an API key and App ID from the developer dashboard.
Save your credentials for later use.
Configure API Credentials in Django

Store credentials in settings.py:


      UMS_API_KEY = "your_api_key"
UMS_APP_ID = "your_app_id"
UMS_API_URL = "https://comms.umeskiasoftwares.com/api/v1/sms/send"
UMS_SENDER_ID = "UMS_SMS"


      Create a Django View to Send SMS

Define a Django form and view for SMS sending.
Handle API Response

Process and return JSON feedback based on the API's response.
    # sms/forms.py
from django import forms

class SMSForm(forms.Form):
    phone = forms.CharField(max_length=15, required=True, label="Phone Number")
    message = forms.CharField(widget=forms.Textarea, required=True, label="Message")

# sms/views.py
import requests
from django.conf import settings
from django.shortcuts import render
from django.http import JsonResponse
from .forms import SMSForm

def send_sms(request):
    if request.method == "POST":
        form = SMSForm(request.POST)
        if form.is_valid():
            phone = form.cleaned_data['phone']
            message = form.cleaned_data['message']

            # Append default STOP message
            default_message = "\n\n\nMuindi\nSTOP *456*9*5#"
            message += default_message

            # Send SMS request
            response = requests.post(settings.UMS_API_URL, data={
                'api_key': settings.UMS_API_KEY,
                'app_id': settings.UMS_APP_ID,
                'sender_id': settings.UMS_SENDER_ID,
                'message': message,
                'phone': phone
            })

            result = response.json()

            # Return JSON response
            if result.get('status') == 'success':
                return JsonResponse({"message": "SMS sent successfully!"})
            else:
                return JsonResponse({"message": "Failed to send SMS."})
    else:
        form = SMSForm()

    return render(request, "sms/send_sms.html", {"form": form})

# sms/urls.py
from django.urls import path
from .views import send_sms

urlpatterns = [
    path("send/", send_sms, name="send_sms"),
]

# templates/sms/send_sms.html
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Send SMS</title>
</head>
<body>
    <h2>Send an SMS</h2>
    <form method="POST">
        {% csrf_token %}
        {{ form.as_p }}
        <button type="submit">Send SMS</button>
    </form>
</body>
</html>

    `
    },    
    
    {
      id: 4,
      title: "Mailers",
      content: `
    # Python Mailers Guide

    Overview
Sending emails in Django is done using the built-in send_mail() function, which supports:
✅ Sending emails via SMTP (e.g., Gmail, Outlook, custom mail servers)
✅ Sending HTML emails
✅ Adding attachments
✅ Using authentication with secure protocols (TLS/SSL)


Step 1: Configuring Email Settings
Add SMTP settings in settings.py:

# settings.py
EMAIL_BACKEND = "django.core.mail.backends.smtp.EmailBackend"
EMAIL_HOST = "smtp.example.com"  # Replace with your SMTP server
EMAIL_PORT = 587  # TLS port
EMAIL_USE_TLS = True
EMAIL_HOST_USER = "your_email@example.com"
EMAIL_HOST_PASSWORD = "your_password"
DEFAULT_FROM_EMAIL = EMAIL_HOST_USER


Step 2: Creating a Django View to Send Email
Create a form and a view to handle email sending.

Define the Email Form

# mailer/forms.py
from django import forms

class EmailForm(forms.Form):
    recipient = forms.EmailField(label="Recipient Email")
    subject = forms.CharField(max_length=255, label="Subject")
    message = forms.CharField(widget=forms.Textarea, label="Message")


    Create the View to Handle Email Sending
# mailer/views.py
from django.core.mail import send_mail
from django.conf import settings
from django.shortcuts import render
from django.http import JsonResponse
from .forms import EmailForm

def send_email(request):
    if request.method == "POST":
        form = EmailForm(request.POST)
        if form.is_valid():
            recipient = form.cleaned_data['recipient']
            subject = form.cleaned_data['subject']
            message = form.cleaned_data['message']

            # Sending the email
            try:
                send_mail(
                    subject,
                    message,
                    settings.DEFAULT_FROM_EMAIL,
                    [recipient],
                    fail_silently=False,
                )
                return JsonResponse({"message": "Email sent successfully!"})
            except Exception as e:
                return JsonResponse({"message": f"Email failed to send: {str(e)}"})

    else:
        form = EmailForm()

    return render(request, "mailer/send_email.html", {"form": form})


    Step 3: Define the URL for Sending Emails

    # mailer/urls.py
from django.urls import path
from .views import send_email

urlpatterns = [
    path("send-email/", send_email, name="send_email"),
]


Step 4: Create an Email Form in HTML

<!-- templates/mailer/send_email.html -->
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Send Email</title>
</head>
<body>
    <h2>Send an Email</h2>
    <form method="POST">
        {% csrf_token %}
        {{ form.as_p }}
        <button type="submit">Send Email</button>
    </form>
</body>
</html>

Step 5: Running the Script

Start the Django development server:


python manage.py runserver

Then, visit:
📌 http://127.0.0.1:8000/send-email/

Now, your Django app can send emails via SMTP
    `
    }
    
  ];

  const toggleExpand = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const handleClickOutside = (event) => {
    if (contentRef.current && !contentRef.current.contains(event.target)) {
      setExpanded(null);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="php-documentation">
      <h1 className="typing-text">{displayedText}</h1>

      <div className="service-list">
        {services.map((service) => (
          <div key={service.id} className="service-item">
            <button className="service-btn" onClick={() => toggleExpand(service.id)}>
              {service.id}. {service.title} <FaArrowRight className="arrow-icon" />
            </button>

            {expanded === service.id && (
              <div className="service-content" ref={contentRef}>
                <div className="content-wrapper">
                  <pre>
                    <code>{service.content}</code>
                  </pre>
                  
                </div>
                <div className="navigation-buttons">
                  <button className="nav-btn" onClick={() => setExpanded(service.id - 1)}>
                    <FaArrowLeft /> Back Service
                  </button>
                  <button className="nav-btn" onClick={() => setExpanded(service.id + 1)}>
                  Next Service <FaNext />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Python;